import { FC, Fragment, useState } from 'react';
import { OutboundModel, PackageDetails as Package } from 'ts/package/interfaces/PackageDetails';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { formatOccupancySrtByDeal, getDealType } from 'utils/package';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import TicketBaggagePac from '../Book/TicketBaggagePac';
import { ReactCountryFlag } from 'react-country-flag';
import { faCheck, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import SendRequestModal from './SendRequestModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isNetravel, isTT } from 'constants/generic';

interface PackageDetailsProps {
  dealModel: Package;
  totalPrice: number;
  totalAvgPrice: number;
  inbound?: OutboundModel;
  outbound?: OutboundModel;
  onClickBook: () => void;
}

const PackageDetails: FC<PackageDetailsProps> = ({
  dealModel: { deal, info, arrivalAirport, departureAirport, additionalInfo },
  inbound,
  outbound,
  ...props
}) => {
  
  const [isRequestMailOpen, setIsRequestMailOpen] = useState(false);
  const { i18n, t } = useTranslation();

  const features = deal.listOfDealFeature
    ?.filter(
      (feature, index, self) => !feature.includes('fastOK') && self.indexOf(feature) === index
    )
    .map((feature) => t(feature.match(/[a-zA-Z ]+/g)?.join('') || ''));

  return (
    <Fragment>
      <div className="default-shadow flight-details-item">
        <div className="title-underline title-header-grey text-lg mb-15">
          {t('package.details')}
        </div>
        <div className="flight-details-info position-relative">
          <div className="package-details-block">
            <span className="package-details-title">{t('flight.departure_date')}</span>
            <span className="package-details-text">
              {dateFormat(deal.departureDate, 'EEE - dd MMMM, yyyy')}
            </span>
          </div>
          {!!inbound?.date && (
            <div className="package-details-block">
              <span className="package-details-title">{t('flight.return_date')}</span>
              <span className="package-details-text">
                {dateFormat(inbound.date, 'EEE - dd MMMM, yyyy')}
              </span>
            </div>
          )}
          <div className="package-details-block">
            <span className="package-details-title">{t('package.nights')}</span>
            <span className="package-details-text">
              {deal.dealLength} {t('hotel.nights_up')}
            </span>
          </div>
          {!!departureAirport?.airCityLang && (
            <div className="package-details-block">
              <span className="package-details-title">{t('flight.from')}</span>
              <span className="package-details-text">
                <ReactCountryFlag className="mr-5" svg countryCode={departureAirport.countryCode} />
                <span>
                  {departureAirport.airCityLang.desc} - {departureAirport.code}{' '}
                </span>
                <span className="text-normal">{departureAirport.desc}</span>
              </span>
            </div>
          )}

          {!!arrivalAirport?.airCityLang && (
            <div className="package-details-block">
              <span className="package-details-title">{t('flight.to')}</span>
              <span className="package-details-text">
                <ReactCountryFlag className="mr-5" svg countryCode={arrivalAirport.countryCode} />

                <span>
                  {arrivalAirport.airCityLang.desc} - {arrivalAirport.code}{' '}
                </span>
                <span className="text-normal">{arrivalAirport.desc}</span>
              </span>
            </div>
          )}
          <div className="package-details-block">
            <span className="package-details-title">{t('hotel.hotel')}</span>
            <span className="package-details-text text-cap">
              <span>{info.hotelName?.toLowerCase()}</span>
              <Rating value={info.hotelRate} className="package-details-rating" />
            </span>
          </div>
          <div className="package-details-block">
            {!!inbound && !!outbound && (
              <Fragment>
                <span className="package-details-title">{t('filter.duration')}</span>

                <span className="package-details-text">
                  <span>
                    {outbound.flightDuration} {`/ ${inbound.flightDuration}`}
                  </span>
                </span>
              </Fragment>
            )}
          </div>
          <div className="package-details-block">
            {!!inbound && !!outbound && (
              <Fragment>
                <span className="package-details-title">{t('filter.airline')}</span>
                <span className="package-details-text">
                  <span>
                    {outbound.airline} {`/ ${inbound.airline}`}
                  </span>
                </span>
              </Fragment>
            )}
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('package.package_type')}</span>
            <span className="package-details-text text-cap">
              <span className="text-normal">
                ({additionalInfo.cityFullDescription ?? info.hotelLocation?.cityName?.toLowerCase()}
                )
              </span>
              <span> {t(getDealType(deal.dealType, 1)?.toLowerCase())} </span>
              {/* <span className="text-contrast">{deal.dealTitle?.toLowerCase()}</span> */}
            </span>
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('package.occupancy')}</span>
            <span className="package-details-text">
              <span className="text-normal">{formatOccupancySrtByDeal(deal)}</span>
            </span>
          </div>
          {!!features?.length && (
            <div className="package-details-block">
              <span className="package-details-title">{t('package.deal_features')}</span>
              <span className="package-details-text">{features.join(', ')}</span>
            </div>
          )}
          {!!deal.provider && !isNetravel && !isTT && (
            <div className="package-details-block">
              <span className="package-details-title">{t('package.provider')}</span>
              <span className="package-details-text">{deal.provider}</span>
            </div>
          )}
          {deal.fastOK && (
            <span
              className="mt-5 text-center text-success"
              style={{
                position: 'absolute',
                top: -7,
                left: i18n.language === 'he' ? 0 : 'auto',
                right: i18n.language === 'he' ? 'auto' : 0,
              }}
            >
              <FontAwesomeIcon icon={faCheck} /> <b>{t('package.immediate_approval')}</b>
            </span>
          )}

          <div className="display-flex flex-between package-details-send">
            <div>
              {!!props.totalPrice && (
                <div className="package-details-block">
                  <span className="package-details-title">{t('package.total_price')}</span>
                  <span className="package-details-text">
                    {formatPriceCurrency(props.totalPrice, getCurrency(deal.currency))}
                  </span>
                </div>
              )}
              {!!props.totalAvgPrice && (
                <div className="package-details-block">
                  <span className="package-details-title">{t('package.price_per_person')}</span>
                  <span className="package-details-text text-lg text-contrast">
                    {formatPriceCurrency(props.totalAvgPrice, getCurrency(deal.currency))}
                  </span>
                </div>
              )}
            </div>
            {isNetravel && (
              <div>
                <Button
                  title=" שלח בקשה"
                  icon={faEnvelope}
                  iconclassname="mr-5"
                  onClick={() => setIsRequestMailOpen(true)}
                />
                <Button
                  title={t('hotel.book')}
                  onClick={() => props.onClickBook()}
                  className="ml-10 btn btn-primary"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="default-shadow flight-details-item pac-baggage display-flex">
        <TicketBaggagePac dealBaggage={deal?.dealBaggage} />
      </div>

      {isRequestMailOpen && <SendRequestModal onClose={() => setIsRequestMailOpen(false)} />}
    </Fragment>
  );
};

export default withErrorBoundary(PackageDetails, 'PackageDetails');
